import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField, Typography } from '@mui/material';
import { PaintBrush } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface ShortTextSettingsProps {
  node: INode;
}

const ShortTextSchema = z.object({
  text: z.string(),
  fontFamily: z.string().default('Roboto, sans-serif'),
  fontSize: z.number().min(8).max(72).default(16),
  isBold: z.boolean().default(false),
  isItalic: z.boolean().default(false),
  isUnderlined: z.boolean().default(false),
  textAlign: z.enum(['left', 'center', 'right', 'justify']).default('left'),
  shortTextColor: z.string().default('#1F2937'),
  backgroundColor: z.string().default('#ffffff'),
  borderColor: z.string().default('#ccc'),
  borderRadius: z.number().min(4).max(72).default(4),
  borderWeight: z.number().min(1).max(10).default(1),
  verticalAlign: z.enum(['top', 'center', 'bottom']).default('top'),
  padding: z.number().min(0).max(72).default(16),
});

export function ShortTextSettings({ node }: ShortTextSettingsProps) {
  const { dispatch } = useFormBuilder();
  const [activeTab, setActiveTab] = useState(0);
  const styles = node?.data?.styles;

  const formsMethods = useForm({
    defaultValues: {
      text: node?.data?.text || 'Short text content',
      fontFamily: styles?.shortText?.fontFamily || 'Roboto, sans-serif',
      fontSize: styles?.shortText?.fontSize || 16,
      isBold: styles?.shortText?.isBold || false,
      isItalic: styles?.shortText?.isItalic || false,
      isUnderlined: styles?.shortText?.isUnderlined || false,
      textAlign: styles?.shortText?.textAlign || 'left',
      shortTextColor: styles?.shortText?.color || '#1F2937',
      backgroundColor: styles?.backgroundColor || '#ffffff',
      borderColor: styles?.borderColor || '#ccc',
      borderRadius: styles?.borderRadius || 4,
      borderWeight: styles?.borderWeight || 1,
      verticalAlign: styles?.verticalAlign || 'top',
      padding: styles?.padding !== undefined ? styles.padding : 16,
    },
    resolver: zodResolver(ShortTextSchema),
  });

  const handleFormChange = (data: any) => {
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        text: data.text,
        styles: {
          shortText: {
            fontFamily: data.fontFamily,
            fontSize: data.fontSize,
            isBold: data.isBold,
            isItalic: data.isItalic,
            isUnderlined: data.isUnderlined,
            textAlign: data.textAlign,
            color: data.shortTextColor,
          },
          backgroundColor: data.backgroundColor,
          borderColor: data.borderColor,
          borderRadius: data.borderRadius,
          borderWeight: data.borderWeight,
          verticalAlign: data.verticalAlign,
          padding: data.padding,
        },
      },
    };

    dispatch({
      type: ActionTypes.UPDATE_NODES_DATA,
      payload: {
        id: node.id,
        updates: { data: updatedNode.data },
      },
    });
  };

  useEffect(() => {
    const subscription = formsMethods.watch(data => {
      if (data && Object.keys(data).length > 0) {
        handleFormChange(data);
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Short Text Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px' }}>
              <Controller
                name="text"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Short Text"
                    placeholder="Enter short text content..."
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    multiline
                    rows={23}
                    sx={{
                      '& .MuiInputBase-root': {
                        margin: '0px',
                        fontSize: '14px',
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <CustomizationForm
                control={formsMethods.control}
                type={ITypeNode.SHORT_TEXT}
              />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
